import i18n from "@/plugins/i18n";
import {
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  format,
  formatISO,
} from "date-fns";
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import pt from "date-fns/locale/pt-BR";
const timezoneMap: Record<string, string> = {
  // GMT-11 a GMT-10
  "Pacific/Midway": "GMT-11",
  "Pacific/Pago_Pago": "GMT-11",
  "Pacific/Honolulu": "GMT-10",

  // GMT-8
  "America/Anchorage": "GMT-8",
  "America/Juneau": "GMT-8",

  // GMT-7
  "America/Vancouver": "GMT-7",
  "America/Los_Angeles": "GMT-7",
  "America/Tijuana": "GMT-7",
  "US/Arizona": "GMT-7",
  "America/Mazatlan": "GMT-7",
  "America/Yukon": "GMT-7",

  // GMT-6
  "America/Edmonton": "GMT-6",
  "America/Denver": "GMT-6",
  "America/Regina": "GMT-6",
  "America/Mexico_City": "GMT-6",
  "America/Guatemala": "GMT-6",
  "America/El_Salvador": "GMT-6",
  "America/Managua": "GMT-6",
  "America/Costa_Rica": "GMT-6",
  "America/Tegucigalpa": "GMT-6",
  "America/Chihuahua": "GMT-6",
  "America/Monterrey": "GMT-6",

  // GMT-5
  "America/Winnipeg": "GMT-5",
  "America/Chicago": "GMT-5",
  "America/Panama": "GMT-5",
  "America/Bogota": "GMT-5",
  "America/Lima": "GMT-5",
  "America/Rio_Branco": "GMT-5",

  // GMT-4
  "America/Montreal": "GMT-4",
  "America/New_York": "GMT-4",
  "America/Indianapolis": "GMT-4",
  "America/Puerto_Rico": "GMT-4",
  "America/Caracas": "GMT-4",
  "America/Santiago": "GMT-4",
  "America/La_Paz": "GMT-4",
  "America/Guyana": "GMT-4",

  // GMT-3 a GMT-2:30
  "America/Halifax": "GMT-3",
  "America/Montevideo": "GMT-3",
  "America/Recife": "GMT-3",
  "America/Buenos_Aires": "GMT-3",
  "America/Sao_Paulo": "GMT-3",
  "Canada/Atlantic": "GMT-3",
  "America/St_Johns": "GMT-2:30",

  // GMT-2 a GMT-1
  "America/Noronha": "GMT-2",
  "America/Godthab": "GMT-1",
  "Atlantic/Cape_Verde": "GMT-1",

  // GMT+0
  "Atlantic/Azores": "GMT+0",
  UTC: "GMT+0",
  "Etc/Greenwich": "GMT+0",
  "Atlantic/Reykjavik": "GMT+0",
  "Africa/Nouakchott": "GMT+0",

  // GMT+1
  "Europe/Dublin": "GMT+1",
  "Europe/London": "GMT+1",
  "Europe/Lisbon": "GMT+1",
  "Africa/Casablanca": "GMT+1",
  "Africa/West": "GMT+1",
  "Africa/Algiers": "GMT+1",
  "Africa/Tunis": "GMT+1",

  // GMT+2
  "Europe/Belgrade": "GMT+2",
  "Europe/Sarajevo": "GMT+2",
  "Europe/Oslo": "GMT+2",
  "Europe/Copenhagen": "GMT+2",
  "Europe/Brussels": "GMT+2",
  "Europe/Berlin": "GMT+2",
  "Europe/Amsterdam": "GMT+2",
  "Europe/Rome": "GMT+2",
  "Europe/Stockholm": "GMT+2",
  "Europe/Vienna": "GMT+2",
  "Europe/Luxembourg": "GMT+2",
  "Europe/Paris": "GMT+2",
  "Europe/Zurich": "GMT+2",
  "Europe/Madrid": "GMT+2",
  "Africa/Harare": "GMT+2",
  "Europe/Warsaw": "GMT+2",
  "Europe/Prague": "GMT+2",
  "Europe/Budapest": "GMT+2",
  "Africa/Tripoli": "GMT+2",
  "Africa/Johannesburg": "GMT+2",
  "Africa/Khartoum": "GMT+2",

  // GMT+3
  "Europe/Helsinki": "GMT+3",
  "Africa/Nairobi": "GMT+3",
  "Europe/Sofia": "GMT+3",
  "Europe/Istanbul": "GMT+3",
  "Europe/Athens": "GMT+3",
  "Europe/Bucharest": "GMT+3",
  "Asia/Nicosia": "GMT+3",
  "Asia/Beirut": "GMT+3",
  "Asia/Damascus": "GMT+3",
  "Asia/Jerusalem": "GMT+3",
  "Asia/Amman": "GMT+3",
  "Africa/Cairo": "GMT+3",
  "Europe/Moscow": "GMT+3",
  "Asia/Baghdad": "GMT+3",
  "Asia/Kuwait": "GMT+3",
  "Asia/Riyadh": "GMT+3",
  "Asia/Bahrain": "GMT+3",
  "Asia/Qatar": "GMT+3",
  "Asia/Aden": "GMT+3",
  "Africa/Djibouti": "GMT+3",
  "Africa/Mogadishu": "GMT+3",
  "Europe/Kiev": "GMT+3",
  "Europe/Minsk": "GMT+3",
  "Europe/Chisinau": "GMT+3",

  // GMT+3:30 a GMT+4:30
  "Asia/Tehran": "GMT+3:30",
  "Asia/Dubai": "GMT+4",
  "Asia/Muscat": "GMT+4",
  "Asia/Baku": "GMT+4",
  "Asia/Kabul": "GMT+4:30",

  // GMT+5 a GMT+5:45
  "Asia/Yekaterinburg": "GMT+5",
  "Asia/Karachi": "GMT+5",
  "Asia/Kolkata": "GMT+5:30",
  "Asia/Calcutta": "GMT+5:30",
  "Asia/Colombo": "GMT+5:30",
  "Asia/Kathmandu": "GMT+5:45",

  // GMT+6 a GMT+6:30
  "Asia/Dhaka": "GMT+6",
  "Asia/Almaty": "GMT+6",
  "Asia/Rangoon": "GMT+6:30",

  // GMT+7
  "Asia/Novosibirsk": "GMT+7",
  "Asia/Krasnoyarsk": "GMT+7",
  "Asia/Bangkok": "GMT+7",
  "Asia/Saigon": "GMT+7",
  "Asia/Jakarta": "GMT+7",

  // GMT+8
  "Asia/Irkutsk": "GMT+8",
  "Asia/Shanghai": "GMT+8",
  "Asia/Hong_Kong": "GMT+8",
  "Asia/Taipei": "GMT+8",
  "Asia/Kuala_Lumpur": "GMT+8",
  "Asia/Singapore": "GMT+8",
  "Australia/Perth": "GMT+8",

  // GMT+9 a GMT+9:30
  "Asia/Yakutsk": "GMT+9",
  "Asia/Seoul": "GMT+9",
  "Asia/Tokyo": "GMT+9",
  "Australia/Darwin": "GMT+9:30",
  "Australia/Adelaide": "GMT+9:30",

  // GMT+10 a GMT+10:30
  "Asia/Vladivostok": "GMT+10",
  "Pacific/Guam": "GMT+10",
  "Australia/Brisbane": "GMT+10",
  "Australia/Sydney": "GMT+10",
  "Australia/Hobart": "GMT+10",
  "Australia/Lord_Howe": "GMT+10:30",

  // GMT+11 a GMT+13
  "Asia/Magadan": "GMT+11",
  "Pacific/Guadalcanal": "GMT+11",
  "Pacific/Noumea": "GMT+11",
  "Asia/Kamchatka": "GMT+12",
  "Pacific/Fiji": "GMT+12",
  "Pacific/Auckland": "GMT+12",
  "Pacific/Apia": "GMT+13",
};

export const formatTodayIso = (): string => {
  return formatISO(new Date());
};

export const formatDatePost = (date: string): string => {
  const diffDays = differenceInCalendarDays(new Date(), new Date(date));
  const formatToday =
    i18n.locale === "pt"
      ? format(new Date(date), "'Hoje às 'HH:mm")
      : i18n.locale === "en"
      ? format(new Date(date), "'Today at 'HH:mm")
      : format(new Date(date), "'Hoy a las 'HH:mm");
  const formatYesterday =
    i18n.locale === "pt"
      ? format(new Date(date), "'Ontem às 'HH:mm")
      : i18n.locale === "en"
      ? format(new Date(date), "'Yesterday at 'HH:mm")
      : format(new Date(date), "'Ayer a las 'HH:mm");
  const formatOthersDay =
    i18n.locale === "pt"
      ? format(new Date(date), "dd/MM' às 'HH:mm")
      : i18n.locale === "en"
      ? format(new Date(date), "MM/dd' at 'HH:mm")
      : format(new Date(date), "dd/MM' a las 'HH:mm");
  return diffDays === 0
    ? formatToday
    : diffDays === 1
    ? formatYesterday
    : formatOthersDay;
};

export const formatDateComment = (date: string): string => {
  const diffDays = differenceInCalendarDays(new Date(), new Date(date));
  const formatToday =
    i18n.locale === "pt"
      ? format(new Date(date), "'Hoje, 'HH:mm")
      : i18n.locale === "en"
      ? format(new Date(date), "'Today, 'HH:mm")
      : format(new Date(date), "'Hoy, 'HH:mm");
  const formatOthersDay =
    i18n.locale === "pt"
      ? format(new Date(date), "dd/MM' 'HH:mm")
      : i18n.locale === "en"
      ? format(new Date(date), "MM/dd' 'HH:mm")
      : format(new Date(date), "dd/MM' 'HH:mm");
  return diffDays === 0 ? formatToday : formatOthersDay;
};

export const formatDateLastUpdate = (date: string, sufix = ""): string => {
  const diffMins = differenceInMinutes(new Date(), new Date(date));
  const diffHours = differenceInHours(new Date(), new Date(date));
  const diffDays = differenceInCalendarDays(new Date(), new Date(date));
  const diffWeeks = differenceInWeeks(new Date(), new Date(date));
  const diffMonths = differenceInMonths(new Date(), new Date(date));
  const diffYears = differenceInYears(new Date(), new Date(date));
  return diffDays === 0
    ? diffHours === 0
      ? `${diffMins} ${
          diffMins > 1 ? i18n.t("format.minutes") : i18n.t("format.minute")
        }${sufix}`
      : `${diffHours} ${
          diffHours > 1 ? i18n.t("format.hours") : i18n.t("format.hour")
        }${sufix}`
    : diffDays === 1
    ? `${i18n.t("format.yesterday")}`
    : diffDays < 7
    ? `${diffDays} ${i18n.t("format.days")}${sufix}`
    : diffWeeks === 1
    ? `${diffWeeks} ${i18n.t("format.week")}${sufix}`
    : diffWeeks < 5
    ? `${diffWeeks} ${i18n.t("format.weeks")}${sufix}`
    : diffMonths === 1
    ? `${diffMonths} ${i18n.t("format.month")}${sufix}`
    : diffMonths < 12
    ? `${diffMonths} ${i18n.t("format.months")}${sufix}`
    : diffYears === 1
    ? `${diffYears} ${i18n.t("format.year")}${sufix}`
    : `${diffYears} ${i18n.t("format.years")}${sufix}`;
};

export const formatDayMonth = (date: string): string => {
  return i18n.locale === "en"
    ? format(new Date(date), "MMM/dd")
    : format(new Date(date), "dd/MMM", { locale: pt });
};

export const formatDateLastChatUpdate = (date: string): string => {
  const diffDays = differenceInCalendarDays(new Date(), new Date(date));
  return diffDays === 0
    ? `${i18n.t("misc.today")}`
    : diffDays === 1
    ? `${i18n.t("misc.yesterday")}`
    : i18n.locale === "en"
    ? format(new Date(date), "MM/dd/yyyy")
    : format(new Date(date), "dd/MM/yyyy", { locale: pt });
};

export const formatDayMonthYear = (date: string): string => {
  return i18n.locale === "en"
    ? format(new Date(date), "MM/dd/yyyy")
    : format(new Date(date), "dd/MM/yyyy", { locale: pt });
};

export const formatTimezone = (
  date: string,
  hour: string,
  timezone: string
): string => {
  return formatISO(zonedTimeToUtc(date + " " + hour, timezone));
};
export const formatTimezoneToUTC = (
  date: string,
  hour: string,
  timezone: string
): string => {
  return new Date(zonedTimeToUtc(date + " " + hour, timezone)).toISOString();
};

export const differenceDays = (date: string): number => {
  return differenceInCalendarDays(new Date(date), new Date());
};

export const utcDateToZoonedTime = (date: string, timezone: string): string => {
  return formatISO(utcToZonedTime(date, timezone));
};

export const dateToZonedTime = (date: string, timezone: string): string => {
  return formatInTimeZone(date, timezone, "yyyy-MM-dd'T'HH:mm:ssXXX");
};

export const formatDuration = (minutes: number): string => {
  if (!minutes) return "0 " + i18n.t("format.minutes");

  if (minutes < 60) {
    return `${minutes} ${i18n.t("format.minutes")}`;
  } else {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${hours} ${
        hours === 1 ? i18n.t("format.hour") : i18n.t("format.hours")
      }`;
    } else {
      return `${hours} ${
        hours === 1 ? i18n.t("format.hour") : i18n.t("format.hours")
      } ${i18n.t("post.and")} ${remainingMinutes} ${i18n.t("format.minutes")}`;
    }
  }
};

/**
 * Formata o horário de acordo com o timezone fornecido e exibe a abreviação do timezone
 * @param dateTimeUTC Data e hora em formato UTC ISO
 * @param timezone Timezone no formato 'America/Sao_Paulo'
 * @returns Horário formatado com a abreviação do timezone (ex: 15:42 GMT-3)
 */
export const formatTimeWithTimezone = (
  dateTimeUTC: string,
  timezone: string
): string => {
  if (!dateTimeUTC || !timezone) return "";

  // Converte o horário UTC para o timezone especificado
  const zonedTime = utcToZonedTime(dateTimeUTC, timezone);

  // Formata apenas o horário (HH:mm)
  const timeFormatted = format(zonedTime, "HH:mm");

  // Obtém a abreviação do timezone ou calcula com base no offset
  const timezoneFormatted = timezoneMap[timezone] || "";

  return `${timeFormatted} ${timezoneFormatted}`;
};

export const formatHour = (dateTimeUTC: string, timezone: string): string => {
  if (!dateTimeUTC || !timezone) return "";
  const zonedTime = utcToZonedTime(dateTimeUTC, timezone);
  return format(zonedTime, "HH:mm");
};

export const formatTime = (date: string, timezone: string): string => {
  const zonedTime = utcToZonedTime(date, timezone);
  return i18n.locale === "en"
    ? format(zonedTime, "MM/dd/yyyy - HH:mm")
    : format(zonedTime, "dd/MM/yyyy - HH:mm");
};

/**
 * Formata data e hora completa com timezone no formato DD/MM/YYYY - HH:MMGMT+X
 * @param date Data no formato ISO (ex: 2025-09-21)
 * @param time Horário no formato HH:mm (ex: 13:22)
 * @param timezone Timezone no formato 'America/Sao_Paulo'
 * @returns Data e hora formatada com timezone (ex: 21/09/2025 - 13:22GMT+13)
 */
export const formatDateTimeWithTimezone = (
  date: string,
  time: string,
  timezone: string
): string => {
  if (!date || !time || !timezone) return "";

  const [year, month, day] = date.split("-");
  const dateFormatted =
    i18n.locale === "en"
      ? `${month}/${day}/${year}`
      : `${day}/${month}/${year}`;

  const timezoneFormatted = timezoneMap[timezone];

  return `${dateFormatted} - ${time}${timezoneFormatted}`;
};

export const formatDateForDisplay = (dateString: string) => {
  if (!dateString) return "";
  // dateString vem no formato YYYY-MM-DD do v-date-picker
  const [year, month, day] = dateString.split("-");
  // Se idioma for inglês, retorna MM/DD/YYYY, caso contrário DD/MM/YYYY
  return i18n.locale === "en"
    ? `${month}/${day}/${year}`
    : `${day}/${month}/${year}`;
};
